import Link from 'next/link'
import { useState } from 'react'

import { BalanceAlias, PaymentDetailInvoice } from '@/api/models/finance'
import { YandexMetrikaEvent } from '@/app/constants/metrics'
import { cn } from '@/app/utils'
import ChevronIcon from '@/assets/svg/ChevronIcon.svg'
import { useOpenInvoiceFile } from '@/components/entities/finance/hooks/useOpenInvoiceFile'
import InfoModal from '@/components/shared/modal/ui/InfoModal'
import { CardSkeleton } from '@/components/shared/skeleton'
import { useYandexMetrika } from '@/components/shared/yandex-metrika/hooks/useYandexMetrika'
import { formatRuble } from '@/utils/number'

type Props = {
  invoices: PaymentDetailInvoice[]
  totalSum: number
  isLoading: boolean
  balanceAlias: BalanceAlias
  name: string
  premiseName: string
}
export const ReceiptCard = ({
  invoices,
  totalSum,
  isLoading,
  balanceAlias,
  name,
  premiseName
}: Props) => {
  const [isExpanded, setExpanded] = useState(false)
  const { reachGoal } = useYandexMetrika()

  const { isModalOpen, setIsModalOpen, handleOpenFile } = useOpenInvoiceFile()

  const isInvoiceOne = invoices?.length === 1

  const onDocumentClick = () => {
    if (
      invoices[0]?.fileUrl &&
      balanceAlias === BalanceAlias.ComplementaryServices
    ) {
      handleOpenFile(invoices[0]?.id)
      reachGoal(YandexMetrikaEvent.INV2_VIEW_DOC)
    }
  }

  const getInvoiceName = (invoice: PaymentDetailInvoice) => {
    if (balanceAlias === BalanceAlias.ComplementaryServices) {
      return invoice.name
    }
    return isInvoiceOne ? '' : invoice.premiseName
  }

  if (isLoading) {
    return (
      <div className="px-40 lg:px-20 sm:p-0">
        <CardSkeleton className="mb-40 h-[144px] w-full xxs:h-[108px]" />
      </div>
    )
  }

  return (
    <>
      <div className="mx-40 mb-20 rounded-16 bg-grey-10 p-24 lg:mx-24 sm:mx-0">
        <button
          className={cn(
            'mb-4 flex w-full items-start justify-between text-left',
            isExpanded && 'mb-16'
          )}
          onClick={() => !isInvoiceOne && setExpanded(!isExpanded)}
        >
          <div>
            <p className="font-bold text-24">{name}</p>
            <p className="text-grey-80 text-16">{premiseName}</p>
          </div>
          {!isInvoiceOne && (
            <button
              aria-label={`${
                isExpanded ? 'Скрыть' : 'Раскрыть'
              } подробности счёта`}
              onClick={() => setExpanded(!isExpanded)}
            >
              <ChevronIcon
                className={cn(
                  'mt-10 rotate-180 transition-transform group-hover:translate-y-2',
                  isExpanded && '-scale-100 group-hover:-translate-y-2'
                )}
              />
            </button>
          )}
        </button>
        {isExpanded &&
          invoices.map((invoice, index) => (
            <div key={invoice.premiseName}>
              <div className="flex justify-between">
                <p className="text-grey-60 text-16">
                  {balanceAlias === BalanceAlias.ComplementaryServices
                    ? invoice.name
                    : invoice.premiseName}
                </p>
                <p className="font-bold text-16">
                  {formatRuble(invoice.totalSum)}
                </p>
              </div>
              {index !== invoices.length - 1 && (
                <hr className="my-12 text-grey-20" />
              )}
            </div>
          ))}
        {isInvoiceOne && (
          <div className="flex flex-col">
            <p className="text-grey-80 text-16">
              {getInvoiceName(invoices[0])}
            </p>
            {invoices[0]?.fileUrl && invoices[0].totalSum > 0 && (
              <Link
                href={invoices[0]?.fileUrl || ''}
                target="_blank"
                className="font-bold text-bronze-40 underline text-16"
                onClick={onDocumentClick}
              >
                {balanceAlias === BalanceAlias.ComplementaryServices
                  ? 'Счёт на оплату'
                  : 'Платёжный документ'}
              </Link>
            )}
          </div>
        )}
        {totalSum > 0 && (
          <>
            <hr className="my-16 text-grey-20" />
            <div className="flex justify-between">
              <p className="text-18">Сумма</p>
              <p className="font-bold text-24">{formatRuble(totalSum)}</p>
            </div>
          </>
        )}
      </div>
      <InfoModal
        headerText="Файл не найден"
        text="Обратитесь в Службу комфорта Sminex"
        visible={isModalOpen}
        setVisibility={setIsModalOpen}
      />
    </>
  )
}
